import type {
  T_ChatV8Event,
  T_ChatV8EventPayload,
  T_ChatV8KnownKindPayload,
  T_ChatV8MessagePartRole,
  T_ChatV8MessagePartWidgetPayload,
  T_ChatV8RenderableKind,
  T_ChatV8WidgetArtifactPayload,
  T_ChatV8WidgetArtifactSnapshotResponse,
  T_ChatV8WidgetArtifactStubPayload,
} from "../types/chat-v8";
import {
  isChatV8PayloadKind,
  isChatV8UnknownEventPayload,
  z_ChatV8KnownKindPayload,
  z_ChatV8WidgetArtifactPayload,
  z_ChatV8WidgetArtifactStubPayload,
} from "../types/chat-v8";

export type T_ChatV8RenderableItemKind = T_ChatV8RenderableKind | "unknown";

export const CHAT_V8_MATERIALIZER_CLASSIFICATION_BY_PAYLOAD_KIND = {
  compaction_marker: "compaction_marker",
  compose_response_validation: "unknown",
  elicitation: "elicitation",
  elicitation_answered: "unknown",
  elicitation_waiting: "unknown",
  message_part: "message_part",
  message_part_stream: "unknown",
  tool_card: "tool_card",
  user_text: "user_text",
  widget_artifact: "unknown",
} satisfies Record<
  T_ChatV8KnownKindPayload["kind"],
  T_ChatV8RenderableItemKind
>;

export type T_ChatV8RenderableItem = {
  actionCuid: string | null;
  partCuid: string | null;
  displaySeq: number | null;
  eventCount: number;
  events: T_ChatV8Event[];
  firstSeq: number;
  isLive: boolean;
  itemCuid: string;
  kind: T_ChatV8RenderableItemKind;
  lastSeq: number;
  payload: T_ChatV8EventPayload;
  primaryEvent: T_ChatV8Event;
  sortSeq: number;
  status: string | null;
  text: string;
  toolCallCuid: string | null;
  type: string;
};

export function getChatV8RenderableItemKey(item: T_ChatV8RenderableItem) {
  if (item.partCuid) {
    return `${item.itemCuid}:${item.partCuid}`;
  }

  return `${item.kind}:${item.itemCuid}:${item.firstSeq}`;
}

type T_RenderableDraft = {
  events: T_ChatV8Event[];
  key: string;
};

export type T_ChatV8WidgetSnapshotCache = ReadonlyMap<
  string,
  T_ChatV8WidgetArtifactSnapshotResponse
>;

export function compareChatV8Events(left: T_ChatV8Event, right: T_ChatV8Event) {
  if (left.seq !== right.seq) {
    return left.seq - right.seq;
  }

  const leftLiveSequence = getMessagePartLiveSequence(left.payload);
  const rightLiveSequence = getMessagePartLiveSequence(right.payload);
  if (leftLiveSequence !== rightLiveSequence) {
    return leftLiveSequence - rightLiveSequence;
  }

  const createdAtCompare = left.createdAt.localeCompare(right.createdAt);
  if (createdAtCompare !== 0) {
    return createdAtCompare;
  }

  return left.eventCuid.localeCompare(right.eventCuid);
}

function getUnknownPayloadValue(payload: T_ChatV8EventPayload, key: string) {
  if (!isChatV8UnknownEventPayload(payload)) {
    return undefined;
  }
  return payload[key];
}

function getMessagePartLiveSequence(payload: T_ChatV8EventPayload) {
  if (
    !isChatV8PayloadKind(payload, "message_part") ||
    !("liveSequence" in payload)
  ) {
    return 0;
  }
  return payload.liveSequence ?? 0;
}

// The developer lab uses these helpers to inspect both known payloads and the
// explicit unknown fallback without weakening product renderers.
export function getPayloadNumber(payload: T_ChatV8EventPayload, key: string) {
  if (key === "liveSequence") {
    return getMessagePartLiveSequence(payload);
  }
  if (key === "durationMs" && isChatV8PayloadKind(payload, "tool_card")) {
    return payload.durationMs ?? 0;
  }
  const value = getUnknownPayloadValue(payload, key);
  return typeof value === "number" ? value : 0;
}

export function getPayloadString(payload: T_ChatV8EventPayload, key: string) {
  if (isChatV8PayloadKind(payload, "user_text") && key === "text") {
    return payload.text;
  }
  if (isChatV8PayloadKind(payload, "message_part")) {
    if (key === "partKind") return payload.partKind;
    if (key === "messagePartRole") return payload.messagePartRole ?? "";
    if (payload.partKind === "markdown") {
      if (key === "markdown") return payload.markdown;
      if (key === "delta" && "delta" in payload) return payload.delta;
    } else {
      if (key === "state") return payload.state;
      if (key === "widgetArtifactCuid") {
        return payload.widgetArtifactCuid ?? "";
      }
      if (key === "widgetType") return payload.widgetType;
    }
  }
  if (isChatV8PayloadKind(payload, "tool_card")) {
    if (key === "phase") return payload.phase;
    if (key === "state") return payload.state;
    if (key === "title") return payload.title;
  }
  if (isChatV8PayloadKind(payload, "elicitation") && key === "title") {
    return payload.title;
  }
  if (isChatV8PayloadKind(payload, "compaction_marker") && key === "summary") {
    return payload.summary;
  }
  const value = getUnknownPayloadValue(payload, key);
  return typeof value === "string" ? value : "";
}

export function getChatV8MessagePartRole(
  payload: T_ChatV8EventPayload,
): T_ChatV8MessagePartRole {
  if (!isChatV8PayloadKind(payload, "message_part")) {
    return "final";
  }
  return payload.messagePartRole === "thinking" ? "thinking" : "final";
}

export function mergeChatV8Events(
  existingEvents: T_ChatV8Event[],
  incomingEvents: T_ChatV8Event[],
) {
  const eventByCuid = new Map<string, T_ChatV8Event>();

  for (const event of existingEvents) {
    eventByCuid.set(event.eventCuid, event);
  }

  for (const event of incomingEvents) {
    eventByCuid.set(event.eventCuid, event);
  }

  return Array.from(eventByCuid.values()).sort(compareChatV8Events);
}

export function stringifyChatV8Payload(value: unknown) {
  if (value === undefined) {
    return "undefined";
  }

  try {
    const result = JSON.stringify(value, null, 2);
    return result ?? String(value);
  } catch {
    return String(value);
  }
}

const TERMINAL_RUN_EVENT_TYPES = new Set([
  "run.cancelled",
  "run.completed",
  "run.failed",
]);

/**
 * Normative post-terminal drop rule: once a run has a terminal event, later
 * events for that run (stray live drafts racing the ~250ms poll, anything
 * that slipped in before the server-side write fence landed) are excluded.
 * The materializer re-runs over the full merged set, so the rule is
 * retroactive: a draft rendered during the race window disappears on the
 * next materialization.
 */
export function dropPostTerminalRunEvents(
  events: T_ChatV8Event[],
): T_ChatV8Event[] {
  const terminalSeqByRunCuid = new Map<string, number>();
  for (const event of events) {
    if (event.runCuid && TERMINAL_RUN_EVENT_TYPES.has(event.type)) {
      const existing = terminalSeqByRunCuid.get(event.runCuid);
      if (existing === undefined || event.seq < existing) {
        terminalSeqByRunCuid.set(event.runCuid, event.seq);
      }
    }
  }
  if (terminalSeqByRunCuid.size === 0) return events;
  return events.filter((event) => {
    if (!event.runCuid) return true;
    const terminalSeq = terminalSeqByRunCuid.get(event.runCuid);
    if (terminalSeq === undefined) return true;
    return event.seq <= terminalSeq || TERMINAL_RUN_EVENT_TYPES.has(event.type);
  });
}

export function materializeChatV8RenderableItems(
  events: T_ChatV8Event[],
  options?: {
    widgetSnapshotsByArtifactCuid?: T_ChatV8WidgetSnapshotCache;
  },
): T_ChatV8RenderableItem[] {
  const droppedEvents = dropPostTerminalRunEvents(events);
  const draftByKey = new Map<string, T_RenderableDraft>();
  const renderableKeyByActionCuid = new Map<string, string>();
  const renderableKeyByItemCuid = new Map<string, string>();

  for (const event of droppedEvents) {
    if (shouldRenderEvent(event) && event.itemCuid) {
      const key = getRenderableKey(event);
      renderableKeyByItemCuid.set(event.itemCuid, key);
      if (event.actionCuid) {
        renderableKeyByActionCuid.set(event.actionCuid, key);
      }
    }
  }

  for (const event of droppedEvents) {
    if (!shouldRenderEvent(event) && !shouldAttachToRenderableItem(event)) {
      continue;
    }

    const eventItemCuid = getEventItemCuid(event);
    const key = event.partCuid
      ? getRenderableKey(event)
      : shouldAttachToRenderableItem(event) && event.actionCuid
        ? (renderableKeyByActionCuid.get(event.actionCuid) ??
          getRenderableKey(event))
        : shouldAttachToRenderableItem(event) && eventItemCuid
          ? (renderableKeyByItemCuid.get(eventItemCuid) ??
            `item:${eventItemCuid}`)
          : getRenderableKey(event);
    const existingDraft = draftByKey.get(key);
    if (existingDraft) {
      existingDraft.events.push(event);
    } else {
      draftByKey.set(key, {
        events: [event],
        key,
      });
    }
  }

  return Array.from(draftByKey.values())
    .map((draft) =>
      materializeDraft({
        draft,
        widgetSnapshotsByArtifactCuid: options?.widgetSnapshotsByArtifactCuid,
      }),
    )
    .filter(isRenderableItem)
    .sort(compareRenderableItems);
}

function compareRenderableItems(
  left: T_ChatV8RenderableItem,
  right: T_ChatV8RenderableItem,
) {
  if (left.sortSeq !== right.sortSeq) {
    return left.sortSeq - right.sortSeq;
  }

  if (left.firstSeq !== right.firstSeq) {
    return left.firstSeq - right.firstSeq;
  }

  return left.itemCuid.localeCompare(right.itemCuid);
}

function getRenderableKey(event: T_ChatV8Event) {
  if (event.partCuid) {
    return `part:${event.partCuid}`;
  }

  if (event.itemCuid) {
    return `item:${event.itemCuid}`;
  }

  return `event:${event.eventCuid}`;
}

function getRenderableKind(event: T_ChatV8Event): T_ChatV8RenderableItemKind {
  const parsed = z_ChatV8KnownKindPayload.safeParse(event.payload);
  if (parsed.success) {
    return CHAT_V8_MATERIALIZER_CLASSIFICATION_BY_PAYLOAD_KIND[
      parsed.data.kind
    ];
  }
  return "unknown";
}

function getRenderableText(args: {
  events: T_ChatV8Event[];
  kind: T_ChatV8RenderableItemKind;
}) {
  switch (args.kind) {
    case "message_part":
      return getMessagePartText(args.events);
    case "user_text":
      return getLatestEventText(args.events);
    case "compaction_marker":
      return getLatestEventText(args.events) || "Thread compacted";
    case "unknown":
      return getLatestEventText(args.events) || "Unsupported chat event";
    default:
      return getLatestEventText(args.events);
  }
}

function getMessagePartText(events: T_ChatV8Event[]) {
  const sortedEvents = [...events].sort(compareChatV8Events);
  const completedEvents = sortedEvents.filter(
    (event) => event.type === "message_part.completed",
  );
  const completedEvent = completedEvents[completedEvents.length - 1] ?? null;
  if (completedEvent) {
    return getEventText(completedEvent);
  }

  const liveEvents = sortedEvents.filter(isMessagePartEvent);
  const liveEvent = liveEvents[liveEvents.length - 1] ?? null;
  return liveEvent ? getEventText(liveEvent) : "";
}

function getEventText(event: T_ChatV8Event) {
  const payload = event.payload;
  if (isChatV8PayloadKind(payload, "user_text")) {
    return payload.text;
  }
  if (isChatV8PayloadKind(payload, "message_part")) {
    if (payload.partKind === "markdown") {
      return payload.markdown || ("delta" in payload ? payload.delta : "");
    }
    const previewTitleValue = payload.preview?.title;
    const previewTitle =
      typeof previewTitleValue === "string" ? previewTitleValue : "";
    return previewTitle || payload.widgetType;
  }
  if (isChatV8PayloadKind(payload, "tool_card")) {
    return payload.title;
  }
  if (isChatV8PayloadKind(payload, "elicitation")) {
    return payload.title;
  }
  if (isChatV8PayloadKind(payload, "compaction_marker")) {
    return payload.summary;
  }
  if (isChatV8UnknownEventPayload(payload)) {
    return (
      getPayloadString(payload, "text") ||
      getPayloadString(payload, "title") ||
      getPayloadString(payload, "description")
    );
  }
  return "";
}

function getFirstNonNullDisplaySeq(events: T_ChatV8Event[]) {
  let result: number | null = null;

  for (const event of events) {
    if (event.displaySeq === null) {
      continue;
    }

    if (result === null || event.displaySeq < result) {
      result = event.displaySeq;
    }
  }

  return result;
}

function getLatestEventText(events: T_ChatV8Event[]) {
  const sortedEvents = [...events].sort(compareChatV8Events);

  for (let index = sortedEvents.length - 1; index >= 0; index -= 1) {
    const event = sortedEvents[index];
    if (!event) {
      continue;
    }
    const text = getEventText(event);
    if (text.length > 0) {
      return text;
    }
  }

  return "";
}

function getLastRenderableEvent(events: T_ChatV8Event[]) {
  const sortedEvents = [...events].sort(compareChatV8Events);

  for (let index = sortedEvents.length - 1; index >= 0; index -= 1) {
    const event = sortedEvents[index];
    if (event && shouldRenderEvent(event)) {
      return event;
    }
  }

  return null;
}

function getMinSeq(events: T_ChatV8Event[]) {
  let result: number | null = null;

  for (const event of events) {
    if (result === null || event.seq < result) {
      result = event.seq;
    }
  }

  return result ?? 0;
}

function getMaxSeq(events: T_ChatV8Event[]) {
  let result: number | null = null;

  for (const event of events) {
    if (result === null || event.seq > result) {
      result = event.seq;
    }
  }

  return result ?? 0;
}

function isMessagePartEvent(event: T_ChatV8Event) {
  return (
    event.type === "message_part.started" ||
    event.type === "message_part.markdown_delta" ||
    event.type === "message_part.snapshot" ||
    event.type === "message_part.updated" ||
    event.type === "message_part.completed" ||
    event.type === "message_part.failed"
  );
}

function isToolCardEvent(event: T_ChatV8Event) {
  return isChatV8PayloadKind(event.payload, "tool_card");
}

function isLiveMessagePartOnly(events: T_ChatV8Event[]) {
  const hasLiveEvent = events.some(
    (event) =>
      isChatV8PayloadKind(event.payload, "message_part") &&
      "live" in event.payload &&
      event.payload.live === true,
  );
  const hasCompletedEvent = events.some(
    (event) =>
      event.type === "message_part.completed" ||
      event.type === "message_part.failed",
  );

  return hasLiveEvent && !hasCompletedEvent;
}

function isRenderableItem(
  item: T_ChatV8RenderableItem | null,
): item is T_ChatV8RenderableItem {
  return item !== null;
}

function materializeDraft(args: {
  draft: T_RenderableDraft;
  widgetSnapshotsByArtifactCuid?: T_ChatV8WidgetSnapshotCache;
}): T_ChatV8RenderableItem | null {
  const draft = args.draft;
  const events = [...draft.events].sort(compareChatV8Events);
  const primaryEvent = getLastRenderableEvent(events);
  if (!primaryEvent) {
    return null;
  }

  const kind = getRenderableKind(primaryEvent);
  const displaySeq = getFirstNonNullDisplaySeq(events);
  const firstSeq = getMinSeq(events);
  const lastSeq = getMaxSeq(events);

  return {
    actionCuid: primaryEvent.actionCuid,
    partCuid: primaryEvent.partCuid,
    displaySeq,
    eventCount: events.length,
    events,
    firstSeq,
    isLive: isLiveMessagePartOnly(events),
    itemCuid: primaryEvent.itemCuid ?? draft.key,
    kind,
    lastSeq,
    payload: buildRenderablePayload({
      events,
      primaryEvent,
      widgetSnapshotsByArtifactCuid: args.widgetSnapshotsByArtifactCuid,
    }),
    primaryEvent,
    sortSeq: displaySeq ?? firstSeq,
    status: primaryEvent.status,
    text: getRenderableText({ events, kind }),
    toolCallCuid: primaryEvent.toolCallCuid,
    type: primaryEvent.type,
  };
}

function buildRenderablePayload(args: {
  events: T_ChatV8Event[];
  primaryEvent: T_ChatV8Event;
  widgetSnapshotsByArtifactCuid?: T_ChatV8WidgetSnapshotCache;
}): T_ChatV8EventPayload {
  const primaryPayload = args.primaryEvent.payload;
  if (
    !isChatV8PayloadKind(primaryPayload, "message_part") ||
    primaryPayload.partKind !== "widget" ||
    !primaryPayload.widgetArtifactCuid
  ) {
    return primaryPayload;
  }

  const artifact = findWidgetArtifactPayload({
    events: args.events,
    snapshot:
      args.widgetSnapshotsByArtifactCuid?.get(
        primaryPayload.widgetArtifactCuid,
      ) ?? null,
    widgetArtifactCuid: primaryPayload.widgetArtifactCuid,
  });
  if (!artifact) {
    return primaryPayload;
  }

  const payload: T_ChatV8MessagePartWidgetPayload = {
    ...primaryPayload,
    ...("snapshot" in artifact ? { snapshot: artifact.snapshot } : {}),
  };
  return payload;
}

type T_MaterializedWidgetArtifact =
  | T_ChatV8WidgetArtifactPayload
  | T_ChatV8WidgetArtifactSnapshotResponse
  | T_ChatV8WidgetArtifactStubPayload;

function findWidgetArtifactPayload(args: {
  events: T_ChatV8Event[];
  snapshot: T_ChatV8WidgetArtifactSnapshotResponse | null;
  widgetArtifactCuid: string;
}): T_MaterializedWidgetArtifact | null {
  if (args.snapshot) {
    return args.snapshot;
  }

  const sortedEvents = [...args.events].sort(compareChatV8Events);

  for (let index = sortedEvents.length - 1; index >= 0; index -= 1) {
    const event = sortedEvents[index];
    if (!event || event.type !== "widget_artifact.generated") {
      continue;
    }

    const parsed = z_ChatV8WidgetArtifactPayload.safeParse(event.payload);
    if (
      parsed.success &&
      parsed.data.widgetArtifactCuid === args.widgetArtifactCuid
    ) {
      return parsed.data;
    }

    const parsedStub = z_ChatV8WidgetArtifactStubPayload.safeParse(
      event.payload,
    );
    if (
      parsedStub.success &&
      parsedStub.data.widgetArtifactCuid === args.widgetArtifactCuid
    ) {
      return parsedStub.data;
    }
  }

  return null;
}

function shouldRenderEvent(event: T_ChatV8Event) {
  if (event.displaySeq !== null) {
    return true;
  }

  return isMessagePartEvent(event) || isToolCardEvent(event);
}

function getEventItemCuid(event: T_ChatV8Event): string | null {
  if (event.itemCuid) return event.itemCuid;
  if (
    event.type === "run.waiting_for_input" &&
    isChatV8PayloadKind(event.payload, "elicitation_waiting")
  ) {
    return event.payload.itemCuid;
  }
  return null;
}

function shouldAttachToRenderableItem(event: T_ChatV8Event) {
  return (
    ((event.type === "elicitation.answered" ||
      event.type === "run.waiting_for_input" ||
      event.type.startsWith("message_part.")) &&
      (getEventItemCuid(event) !== null || event.partCuid !== null)) ||
    (event.type === "widget_artifact.generated" && event.actionCuid !== null)
  );
}
